import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2, RefreshCw } from 'lucide-react'
import useSWR from 'swr'

// 获取广告源颜色
function getSourceColor(sourceName) {
  const lowerName = sourceName.toLowerCase()
  if (lowerName.includes('admob')) return 'bg-blue-500'
  if (lowerName.includes('meta') || lowerName.includes('facebook'))
    return 'bg-blue-700'
  if (lowerName.includes('unity')) return 'bg-gray-600'
  if (lowerName.includes('applovin')) return 'bg-green-600'
  if (lowerName.includes('ironsource')) return 'bg-red-600'
  if (lowerName.includes('vungle')) return 'bg-purple-600'
  if (lowerName.includes('pangle')) return 'bg-orange-600'
  if (lowerName.includes('mintegral')) return 'bg-pink-600'
  return 'bg-gray-500'
}

function PamMappingInfoModal({ isOpen, onClose, group, pid }) {
  const {
    data: mappingInfo,
    error,
    isLoading,
    mutate,
  } = useSWR(
    isOpen && group && pid
      ? [
          `/admob/pam/mediation-groups/${group.id}/mapping-info`,
          {
            pid,
            groupName: group.name,
          },
        ]
      : null,
  )

  const handleRefresh = async () => {
    await mutate()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>
              {group?.name || '中介组'} - Ad Unit Mapping 信息
            </DialogTitle>
            <Button
              onClick={handleRefresh}
              disabled={isLoading}
              variant="outline"
              size="sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-3 text-gray-600">加载中...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">加载失败: {error.message}</p>
            </div>
          )}

          {mappingInfo && (
            <div className="space-y-4">
              {/* 中介组基本信息 */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-700">中介组名称:</span>
                  <span className="text-gray-900">{mappingInfo.groupName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-700">类型:</span>
                  <Badge variant="outline">{mappingInfo.groupType}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-700">
                    Waterfall 总数:
                  </span>
                  <span className="text-gray-900">
                    {mappingInfo.waterfalls.length}
                  </span>
                </div>
              </div>

              {/* Waterfall 列表 */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg text-gray-800">
                  Waterfall 详情
                </h3>

                {mappingInfo.waterfalls.map((waterfall, index) => (
                  <div
                    key={`${waterfall.id}-${index}`}
                    className="border rounded-lg p-4 bg-white space-y-3"
                  >
                    {/* Waterfall 基本信息 */}
                    <div className="flex items-center gap-3">
                      <Badge
                        className={`${getSourceColor(waterfall.name)} text-white`}
                      >
                        {waterfall.name}
                      </Badge>
                      <Badge variant="secondary">${waterfall.price}</Badge>
                      {!waterfall.mappingDetails && (
                        <Badge variant="outline" className="text-gray-500">
                          非 Admob Waterfall
                        </Badge>
                      )}
                    </div>

                    {/* Ad Unit ID */}
                    {waterfall.adUnitId && (
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">
                          Ad Unit ID:{' '}
                        </span>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {waterfall.adUnitId}
                        </code>
                      </div>
                    )}

                    {/* Mapping 详细信息 */}
                    {waterfall.mappingDetails && (
                      <div className="bg-blue-50 rounded-lg p-3 space-y-2 border border-blue-200">
                        <div className="font-semibold text-blue-900 mb-2">
                          📋 Mapping 详情
                        </div>

                        <div className="grid grid-cols-1 gap-2 text-sm">
                          {/* Display Name */}
                          <div>
                            <span className="font-medium text-gray-700">
                              显示名称:{' '}
                            </span>
                            <span className="text-gray-900">
                              {waterfall.mappingDetails.displayName || '未设置'}
                            </span>
                          </div>

                          {/* Adapter ID */}
                          <div>
                            <span className="font-medium text-gray-700">
                              Adapter ID:{' '}
                            </span>
                            <code className="text-xs bg-white px-2 py-1 rounded border">
                              {waterfall.mappingDetails.adapterId || '未知'}
                            </code>
                          </div>

                          {/* Waterfall Ad Unit ID */}
                          {waterfall.mappingDetails.waterfallAdUnitId && (
                            <div>
                              <span className="font-medium text-gray-700">
                                Waterfall Ad Unit ID:{' '}
                              </span>
                              <code className="text-xs bg-white px-2 py-1 rounded border">
                                {waterfall.mappingDetails.waterfallAdUnitId}
                              </code>
                            </div>
                          )}

                          {/* Resource Name */}
                          <div>
                            <span className="font-medium text-gray-700">
                              Resource Name:{' '}
                            </span>
                            <code className="text-xs bg-white px-2 py-1 rounded border break-all">
                              {waterfall.mappingDetails.name}
                            </code>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Mapping Resource Name (如果没有详情但有 resource name) */}
                    {!waterfall.mappingDetails &&
                      waterfall.mappingResourceName && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">
                            Mapping Resource Name:{' '}
                          </span>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded break-all">
                            {waterfall.mappingResourceName}
                          </code>
                        </div>
                      )}
                  </div>
                ))}

                {mappingInfo.waterfalls.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    该中介组暂无 Waterfall 数据
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PamMappingInfoModal

